import React, { forwardRef, useMemo, useLayoutEffect } from "react";

import { useFloating, offset, flip, shift, autoUpdate, limitShift, size, VirtualElement } from "@floating-ui/react";
import { cn } from "@perceptron/utils/cn";
import { Editor } from "@tiptap/core";

import { CaretBubblePosition } from "@demo/extensions/caret-bubble";
import { CARET_BUBBLE_ATTRIBUTE } from "@demo/store/caret-bubble";

interface CaretBubbleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  editor: Editor;
  caretPosition: CaretBubblePosition;
}

/**
 * Creates a virtual element for floating-ui anchored at the end of the caret's
 * line. Coordinates are resolved lazily from the editor view at measure time —
 * the captured `caretPosition` coords are viewport-relative and go stale as
 * soon as the editor pane scrolls, which previously left the bubble pinned to
 * the old screen position. `contextElement` makes floating-ui's `autoUpdate`
 * listen to the pane's scroll ancestors so each scroll triggers a re-measure.
 */
const createVirtualElement = (editor: Editor, caretPosition: CaretBubblePosition): VirtualElement => {
  let contextElement: Element | undefined;
  try {
    contextElement = editor.view.dom;
  } catch {
    contextElement = undefined;
  }

  return {
    contextElement,
    getBoundingClientRect() {
      try {
        const { view } = editor;
        const docSize = view.state.doc.content.size;
        const from = Math.min(caretPosition.from, docSize);
        const $from = view.state.doc.resolve(from);
        const lineEndPos = Math.min($from.start() + $from.parent.content.size, docSize);

        const cursorCoords = view.coordsAtPos(from);
        const endCoords = view.coordsAtPos(lineEndPos);

        return {
          x: endCoords.right,
          y: cursorCoords.top,
          width: 0,
          height: cursorCoords.bottom - cursorCoords.top,
          top: cursorCoords.top,
          right: endCoords.right,
          bottom: cursorCoords.bottom,
          left: endCoords.right,
        };
      } catch {
        // Editor mid-teardown or stale position — fall back to the captured coords.
        return {
          x: caretPosition.lineEndX,
          y: caretPosition.lineCoords.top,
          width: 0,
          height: caretPosition.lineCoords.height,
          top: caretPosition.lineCoords.top,
          right: caretPosition.lineEndX,
          bottom: caretPosition.lineCoords.top + caretPosition.lineCoords.height,
          left: caretPosition.lineEndX,
        };
      }
    },
  };
};

export const CaretBubble = forwardRef<HTMLButtonElement, CaretBubbleProps>(
  ({ className, caretPosition, editor, ...props }, ref) => {
    // Create virtual element for positioning
    const virtualElement = useMemo(() => createVirtualElement(editor, caretPosition), [editor, caretPosition]);

    // Use floating-ui for smart positioning
    const { refs, floatingStyles } = useFloating({
      placement: "right",
      middleware: [
        // Add offset from the text
        offset({ mainAxis: 4, crossAxis: 0 }),
        // Flip to opposite side if no space
        flip({
          fallbackAxisSideDirection: "start",
          padding: 8,
        }),
        // Shift along the axis to stay in viewport
        shift({
          padding: 8,
          limiter: limitShift({
            offset: ({ rects }) => ({
              mainAxis: rects.reference.height / 2,
            }),
          }),
        }),
        // Ensure the button doesn't overflow viewport
        size({
          apply({ availableWidth, availableHeight, elements }) {
            // Button is 16x16px (w-4 h-4)
            const buttonSize = 16;

            Object.assign(elements.floating.style, {
              maxWidth: `${Math.min(buttonSize, availableWidth)}px`,
              maxHeight: `${Math.min(buttonSize, availableHeight)}px`,
            });
          },
          padding: 8,
        }),
      ],
      // Auto-update position on scroll/resize
      whileElementsMounted: autoUpdate,
    });

    // Set virtual element as reference using the refs object
    useLayoutEffect(() => {
      refs.setReference(virtualElement);
    }, [virtualElement, refs]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the click from propagating to the editor
      e.stopPropagation();
      // Call the original onClick if provided
      props.onClick?.(e);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      // Prevent focus changes when clicking the button
      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <button
        {...{ [CARET_BUBBLE_ATTRIBUTE]: "" }}
        aria-label="Insert content"
        className={cn(
          "pointer-events-auto fixed z-[10000] flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-primary bg-white transition-opacity duration-150 ease-out hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 dark:bg-muted",
          className || "",
        )}
        ref={(node) => {
          // Handle both refs
          refs.setFloating(node);
          if (ref) {
            if (typeof ref === "function") {
              ref(node);
            } else {
              ref.current = node;
            }
          }
        }}
        style={floatingStyles}
        {...props}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        <span className="pointer-events-none">
          <svg fill="none" height="8" viewBox="0 0 8 8" width="8" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 1.5V6.5M1.5 4H6.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1" />
          </svg>
        </span>
      </button>
    );
  },
);

CaretBubble.displayName = "CaretBubble";
